import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Switch,
  VStack,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Checkbox,
  IconButton,
  Flex,
  Badge,
  HStack,
  useToast,
  Box,
  Divider,
  Alert,
  AlertIcon,
} from '@chakra-ui/react';
import { FaPlus, FaTrash, FaFileExcel } from 'react-icons/fa';
import { putData, postData, deleteData } from '../../utils/httpClient';

const CampaignDetailModal = ({
  isOpen,
  onClose,
  campaign,
  onUpdate,
  isNew,
  onCreate,
  onDataChanged,
  readOnly = false,
}) => {
  const [formData, setFormData] = useState({
    code: '',
    description: '',
    active: true,
  });
  const [chassisList, setChassisList] = useState([]);
  const [newChassis, setNewChassis] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isProcessingFile, setIsProcessingFile] = useState(false);
  const toast = useToast();

  useEffect(() => {
    if (campaign && !isNew) {
      setFormData({
        code: campaign.code || '',
        description: campaign.description || '',
        active: campaign.active !== undefined ? campaign.active : false,
      });
      setChassisList(campaign.campaignChassis || []);
    } else if (isNew) {
      setFormData({
        code: '',
        description: '',
        active: true,
      });
      setChassisList([]);
    }
  }, [campaign, isNew, isOpen]);

  const handleSubmit = async () => {
    if (!formData.code.trim() || !formData.description.trim()) {
      toast({
        title: 'Error',
        description: 'Código y descripción son campos obligatorios',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsLoading(true);
    try {
      if (isNew) {
        const chassisNumbers = chassisList.map(c => c.chassisNumber || c);
        await onCreate({
          ...formData,
          chassisNumbers,
        });
      } else {
        // Actualizar información básica de la campaña
        await onUpdate(campaign.id, formData);
        
        // Si hay números de chasis nuevos (strings), agregarlos
        const newChassisNumbers = chassisList
          .filter(c => typeof c === 'string') // Solo los que son strings (nuevos)
          .filter(c => c.trim().length > 0);
        
        if (newChassisNumbers.length > 0) {
          await postData(`/campaigns/${campaign.id}/chassis`, { 
            chassisNumbers: newChassisNumbers 
          });
          
          toast({
            title: 'Números de chasis agregados',
            description: `Se agregaron ${newChassisNumbers.length} números de chasis`,
            status: 'success',
            duration: 3000,
            isClosable: true,
          });
          
          // Recargar la lista de campañas para mostrar el conteo actualizado
          if (onDataChanged) {
            onDataChanged();
          }
        }
      }
      onClose();
    } catch (error) {
      
      let errorMessage = 'Error al guardar la campaña';
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast({
        title: 'Error',
        description: errorMessage,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleExcelUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validar que sea un archivo Excel
    const validTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
      'text/csv'
    ];
    
    if (!validTypes.includes(file.type)) {
      toast({
        title: 'Archivo no válido',
        description: 'Por favor selecciona un archivo Excel (.xlsx, .xls) o CSV',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsProcessingFile(true);

    try {
      // Importar la librería xlsx dinámicamente
      const XLSX = await import('xlsx');
      
      const reader = new FileReader();
      
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          
          // Tomar la primera hoja
          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];
          
          // Convertir a JSON
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
          
          // Extraer números de chasis (primera columna, ignorar header si existe)
          const chassisNumbers = [];
          
          jsonData.forEach((row, index) => {
            if (row.length > 0 && row[0]) {
              const chassisValue = String(row[0]).trim().toUpperCase(); // Convertir a mayúsculas
              // Saltar headers comunes
              if (chassisValue && 
                  !chassisValue.toLowerCase().includes('chasis') &&
                  !chassisValue.toLowerCase().includes('chassis') &&
                  !chassisValue.toLowerCase().includes('numero') &&
                  chassisValue.length > 3) { // Filtrar valores muy cortos
                chassisNumbers.push(chassisValue);
              }
            }
          });

          if (chassisNumbers.length === 0) {
            toast({
              title: 'Sin datos válidos',
              description: 'No se encontraron números de chasis válidos en el archivo',
              status: 'warning',
              duration: 3000,
              isClosable: true,
            });
            return;
          }

          // Eliminar duplicados y combinar con la lista existente
          const existingChassis = chassisList.map(c => 
            typeof c === 'string' ? c : c.chassisNumber
          );
          
          const newUniqueChassis = chassisNumbers.filter(chassis => 
            !existingChassis.includes(chassis)
          );

          const newChassisEntries = newUniqueChassis; // Siempre como strings para identificar nuevos

          setChassisList([...chassisList, ...newChassisEntries]);

          toast({
            title: 'Archivo cargado exitosamente',
            description: `Se agregaron ${newUniqueChassis.length} números de chasis únicos`,
            status: 'success',
            duration: 3000,
            isClosable: true,
          });

        } catch (parseError) {
          console.error('Error al procesar el archivo:', parseError);
          toast({
            title: 'Error al procesar archivo',
            description: 'Hubo un problema al leer el contenido del archivo',
            status: 'error',
            duration: 3000,
            isClosable: true,
          });
        }
      };

      reader.readAsArrayBuffer(file);

    } catch (error) {
      console.error('Error al cargar Excel:', error);
      toast({
        title: 'Error al cargar archivo',
        description: 'Hubo un problema al cargar el archivo Excel',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsProcessingFile(false);
      // Limpiar el input
      event.target.value = '';
    }
  };

  const downloadTemplate = async () => {
    try {
      const XLSX = await import('xlsx');
      
      // Crear datos de ejemplo
      const templateData = [
        ['Numero_Chasis'],
        ['WVW1234567890123'],
        ['WVW1234567890124'],
        ['WVW1234567890125'],
        ['WVW1234567890126'],
        ['WVW1234567890127']
      ];

      // Crear workbook y worksheet
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.aoa_to_sheet(templateData);

      // Agregar worksheet al workbook
      XLSX.utils.book_append_sheet(wb, ws, "Chasis");

      // Descargar archivo
      XLSX.writeFile(wb, "template_chasis_campana.xlsx");

      toast({
        title: 'Template descargado',
        description: 'Se descargó el template de ejemplo para cargar números de chasis',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

    } catch (error) {
      console.error('Error al generar template:', error);
      toast({
        title: 'Error',
        description: 'No se pudo generar el template de ejemplo',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const addChassis = () => {
    if (newChassis.trim()) {
      // Para campañas nuevas, guardar como string
      // Para campañas existentes, también guardar como string para poder identificar los nuevos
      const chassisEntry = newChassis.trim().toUpperCase(); // Convertir a mayúsculas
      
      setChassisList([...chassisList, chassisEntry]);
      setNewChassis('');
    }
  };

  const removeChassis = async (index, chassisId) => {
    if (isNew || !chassisId) {
      // Si es nueva campaña o no tiene ID, solo remover del array
      const newList = chassisList.filter((_, i) => i !== index);
      setChassisList(newList);
    } else {
      // Si tiene ID, eliminar de la base de datos
      try {
        await deleteData(`/campaigns/chassis/${chassisId}`);
        const newList = chassisList.filter((_, i) => i !== index);
        setChassisList(newList);
        toast({
          title: 'Chasis eliminado',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      } catch (error) {
        toast({
          title: 'Error',
          description: 'Error al eliminar el chasis',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
      
      // Recargar la lista de campañas
      if (onDataChanged) {
        onDataChanged();
      }
    }
  };

  const updateChassisReview = async (chassisId, reviewed) => {
    try {
      await putData(`/campaigns/chassis/${chassisId}`, { reviewed });
      const updatedList = chassisList.map(chassis =>
        chassis.id === chassisId ? { ...chassis, reviewed } : chassis
      );
      setChassisList(updatedList);
      toast({
        title: 'Chasis actualizado',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      
      // Recargar la lista de campañas
      if (onDataChanged) {
        onDataChanged();
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Error al actualizar el chasis',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" closeOnOverlayClick={false}>
      <ModalOverlay />
      <ModalContent maxW="800px">
        <ModalHeader>
          {isNew ? 'Nueva Campaña' : `Campaña: ${campaign?.code}`}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody maxH="75vh" overflowY="auto">
          <VStack spacing={4} align="stretch">
            <FormControl isRequired>
              <FormLabel>Código</FormLabel>
              <Input
                value={formData.code}
                isReadOnly={readOnly}
                onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                placeholder="Ej: CAMP001"
              />
            </FormControl>

            <FormControl isRequired>
              <FormLabel>Descripción</FormLabel>
              <Textarea
                value={formData.description}
                isReadOnly={readOnly}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Descripción de la campaña"
                rows={3}
              />
            </FormControl>

            <FormControl display="flex" alignItems="center">
              <FormLabel mb="0">
                Campaña activa
              </FormLabel>
              <Switch
                isChecked={formData.active}
                isDisabled={readOnly}
                onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
              />
            </FormControl>

            {/* Sección de números de chasis */}
            <Box>
              <Text fontSize="lg" fontWeight="bold" mb={3}>
                Números de Chasis
              </Text>
              
              {!readOnly && <VStack spacing={3} align="stretch">
                {/* Carga manual */}
                <HStack>
                  <Input
                    placeholder="Número de chasis"
                    value={newChassis}
                    onChange={(e) => setNewChassis(e.target.value.toUpperCase())}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        addChassis();
                      }
                    }}
                  />
                  <IconButton
                    icon={<FaPlus />}
                    colorScheme="gray"
                    onClick={addChassis}
                  />
                </HStack>

                <Divider />

                {/* Carga desde Excel */}
                <Box>
                  <Text fontSize="md" fontWeight="semibold" mb={2}>
                    Cargar desde Excel
                  </Text>
                  <Alert status="info" size="sm" mb={3}>
                    <AlertIcon />
                    <Box flex="1">
                      <Text fontSize="sm">
                        Selecciona un archivo Excel (.xlsx, .xls) o CSV con los números de chasis en la primera columna.
                      </Text>
                      <Button
                        size="xs"
                        variant="link"
                        colorScheme="gray"
                        onClick={downloadTemplate}
                        mt={1}
                      >
                        Descargar template de ejemplo
                      </Button>
                    </Box>
                  </Alert>
                  
                  <HStack>
                    <Button
                      leftIcon={<FaFileExcel />}
                      colorScheme="gray"
                      variant="outline"
                      size="sm"
                      onClick={() => document.getElementById('excel-upload').click()}
                      isLoading={isProcessingFile}
                      loadingText="Procesando..."
                    >
                      Seleccionar Excel
                    </Button>
                    {chassisList.length > 0 && (
                      <Button
                        leftIcon={<FaTrash />}
                        colorScheme="red"
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setChassisList([]);
                          toast({
                            title: 'Lista limpiada',
                            description: 'Se eliminaron todos los números de chasis',
                            status: 'info',
                            duration: 2000,
                            isClosable: true,
                          });
                        }}
                      >
                        Limpiar Lista
                      </Button>
                    )}
                    <input
                      id="excel-upload"
                      type="file"
                      accept=".xlsx,.xls,.csv"
                      onChange={handleExcelUpload}
                      style={{ display: 'none' }}
                    />
                  </HStack>
                </Box>
              </VStack>}

              {chassisList.length > 0 && (
                <Box mt={4}>
                  <Flex justifyContent="space-between" alignItems="center" mb={3}>
                    <Text fontSize="md" fontWeight="semibold">
                      Lista de Chasis ({chassisList.length} elementos)
                    </Text>
                    <Badge colorScheme="gray" variant="subtle">
                      {chassisList.filter(c => typeof c === 'object' ? c.reviewed : false).length} revisados
                    </Badge>
                  </Flex>
                <Table size="sm" variant="simple">
                  <Thead>
                    <Tr>
                      <Th>Número de Chasis</Th>
                      {!isNew && <Th>Revisado</Th>}
                      <Th>Acciones</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {chassisList.map((chassis, index) => {
                      const chassisNumber = typeof chassis === 'string' ? chassis : chassis.chassisNumber;
                      const chassisId = typeof chassis === 'object' ? chassis.id : null;
                      const isReviewed = typeof chassis === 'object' ? chassis.reviewed : false;

                      return (
                        <Tr key={index}>
                          <Td>{chassisNumber}</Td>
                          {!isNew && (
                            <Td>
                              <Checkbox
                                isChecked={isReviewed}
                                isDisabled={readOnly}
                                onChange={(e) => updateChassisReview(chassisId, e.target.checked)}
                              />
                            </Td>
                          )}
                          <Td>
                            {!readOnly && (
                              <IconButton
                                icon={<FaTrash />}
                                size="sm"
                                colorScheme="red"
                                variant="ghost"
                                onClick={() => removeChassis(index, chassisId)}
                              />
                            )}
                          </Td>
                        </Tr>
                      );
                    })}
                  </Tbody>
                </Table>
                </Box>
              )}
            </Box>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            {readOnly ? 'Cerrar' : 'Cancelar'}
          </Button>
          {!readOnly && (
            <Button variant="adminPrimary" onClick={handleSubmit} isLoading={isLoading}>
              {isNew ? 'Crear Campaña' : 'Guardar Cambios'}
            </Button>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default CampaignDetailModal;
